/**
 * lib/letterPdf.ts
 * Branded A4 company letter on letterhead (jsPDF). Renders the inset
 * orange banner, company contact block, reference + date, recipient,
 * subject, justified body, and a sign-off with optional director
 * signature image and generated company stamp.
 */
import { jsPDF } from 'jspdf';
import type { CompanyDocument, CompanySettings } from '@/types';
import { drawCompanyStamp } from './companyStamp';
import { brandName } from './pdfIcons';

const ORANGE: [number, number, number] = [255, 107, 53];
const CHAR: [number, number, number] = [34, 39, 46];
const GRAY: [number, number, number] = [120, 128, 138];

function fmtDate(iso: string): string {
  return iso ? new Date(iso).toLocaleDateString('en-BW', { day: '2-digit', month: 'long', year: 'numeric' }) : '';
}

export function letterFileName(d: CompanyDocument): string {
  return `${d.reference}.pdf`;
}

export function buildLetterPdf(d: CompanyDocument, company: CompanySettings): jsPDF {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const W = 210;
  const M = 18;
  const right = W - M;
  const bandW = W - 2 * M;

  // ---- Letterhead band (inset, square) ----
  const bandY = 12;
  const bandH = 26;
  doc.setFillColor(...ORANGE);
  doc.rect(M, bandY, bandW, bandH, 'F');
  const cy = bandY + bandH / 2;
  doc.setDrawColor(255, 255, 255);
  doc.setLineWidth(1.4);
  doc.setLineCap('round');
  doc.setLineJoin('round');
  const cW = 3.4;
  const chX = M + 8;
  doc.line(chX, cy - 3.4, chX + cW, cy);
  doc.line(chX + cW, cy, chX, cy + 3.4);
  const wordX = chX + cW + 3;
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(brandName(company), wordX, cy - 0.5);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.text('PROPRIETARY LIMITED', wordX, cy + 4.5);
  // contact block (right, inside band)
  doc.setFontSize(7);
  const contact = [company.physicalAddress, company.phones?.[0], company.email].filter(Boolean) as string[];
  let chy = cy - (contact.length - 1) * 1.7 - 1;
  contact.forEach((l) => {
    doc.text(l, M + bandW - 6, chy, { align: 'right' });
    chy += 3.4;
  });

  // ---- Reference + date ----
  let y = 50;
  doc.setTextColor(...GRAY);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(`Our Ref: ${d.reference}`, M, y);
  doc.text(fmtDate(d.date), right, y, { align: 'right' });

  // ---- Recipient ----
  y += 10;
  doc.setTextColor(...CHAR);
  d.recipient.split('\n').forEach((l) => {
    doc.text(l, M, y);
    y += 5;
  });

  // ---- Subject ----
  y += 4;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  const subjectLines = doc.splitTextToSize(d.subject.toUpperCase(), bandW) as string[];
  subjectLines.forEach((l) => {
    doc.text(l, M, y);
    y += 5;
  });
  doc.setLineWidth(0.3);
  doc.setDrawColor(...CHAR);
  doc.line(M, y - 3.5, M + Math.min(bandW, doc.getTextWidth(subjectLines[0])), y - 3.5);
  y += 3;

  // ---- Body ----
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...CHAR);
  const paras = d.body.split('\n\n');
  paras.forEach((p) => {
    const lines = doc.splitTextToSize(p.replace(/\n/g, ' '), bandW) as string[];
    lines.forEach((l) => {
      doc.text(l, M, y);
      y += 5.2;
    });
    y += 3.5;
  });

  // ---- Sign-off ----
  y += 4;
  doc.text('Yours faithfully,', M, y);
  y += 5.5;
  doc.text(`For and on behalf of ${company.legalName || company.tradingName}`, M, y);
  y += 6;

  const sigTop = y;
  if (d.signed && company.directorSignature) {
    try {
      doc.addImage(company.directorSignature, 'PNG', M, y, 46, 18);
    } catch {
      /* ignore bad image */
    }
  }
  y += 20;
  doc.setDrawColor(...CHAR);
  doc.setLineWidth(0.3);
  doc.line(M, y, M + 60, y);
  y += 5;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text(d.signatoryName || company.directorName || '', M, y);
  y += 4.5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...GRAY);
  doc.text(d.signatoryTitle || company.directorTitle || 'Director', M, y);

  // ---- Stamp (overlaps sign-off area, to the right) ----
  if (d.stamped) {
    drawCompanyStamp(doc, company, M + 95, sigTop + 9, 21);
  }

  // ---- Footer ----
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...GRAY);
  const reg = [
    company.legalName,
    `Reg. ${company.registrationNumber}`,
    company.tin ? `TIN ${company.tin}` : '',
  ]
    .filter(Boolean)
    .join('   ·   ');
  doc.text(reg, W / 2, 288, { align: 'center' });

  return doc;
}

export function downloadLetterPdf(d: CompanyDocument, company: CompanySettings): void {
  buildLetterPdf(d, company).save(letterFileName(d));
}
export function letterPdfBase64(d: CompanyDocument, company: CompanySettings): string {
  const uri = buildLetterPdf(d, company).output('datauristring');
  return uri.slice(uri.indexOf(',') + 1);
}
